import { useState, useEffect, useRef } from "react";
import useApi from "./useApi";

export interface AgentData {
  userId: number;
  displayName: string;
  status: string;
  currentChats: number;
  maxChats: number;
}

export interface TeamData {
  teamId: number;
  teamName: string;
  agentsOnline: number;
  agentsAvailable: number;
  agentsBusy: number;
  openConversations: number;
  unassignedConversations: number;
  longestWaitMinutes: number;
  avgWaitMinutes: number;
  /** "covered" | "no_coverage" | "no_agents" */
  coverageStatus: string;
  agents: AgentData[];
}

export interface DashboardKpis {
  agentsOnline: number;
  agentsAvailable: number;
  agentsBusy: number;
  openConversations: number;
  queuedConversations: number;
  botActive: number;
  resolvedToday: number;
  missedAbandonedToday: number;
  longestWaitMinutes: number;
  avgWaitMinutes: number;
  avgHandleTimeMinutes: number;
  avgFirstResponseMinutes: number;
}

export interface TopAgent {
  name: string;
  resolved: number;
  active: number;
}

export interface ChannelBreakdown {
  channel: string;
  count: number;
  queued: number;
}

export interface DashboardData {
  isManagerView: boolean;
  viewingTeamIds: number[];
  kpis: DashboardKpis;
  topAgents: TopAgent[];
  channelBreakdown: ChannelBreakdown[];
  teams: TeamData[];
}

export type StreamStatus = "connecting" | "live" | "reconnecting" | "error";

const CACHE_KEY = "dashboard_live_cache";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function readCache(): { data: DashboardData; savedAt: number } | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() - cached.savedAt > CACHE_TTL_MS) return null;
    return cached;
  } catch {
    return null;
  }
}

function writeCache(data: DashboardData) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, savedAt: Date.now() }));
  } catch {}
}

export function useLiveDashboard(overrideSiteId?: number) {
  const api = useApi();

  // Seed from sessionStorage so navigating back shows data immediately
  const initialCache = useRef(overrideSiteId ? null : readCache()).current;

  const [data, setData] = useState<DashboardData | null>(initialCache?.data ?? null);
  const [streamStatus, setStreamStatus] = useState<StreamStatus>(
    initialCache ? "reconnecting" : "connecting"
  );
  const [lastUpdated, setLastUpdated] = useState<Date | null>(
    initialCache ? new Date(initialCache.savedAt) : null
  );

  // When switching sites (superadmin), reset to blank slate
  const prevOverride = useRef(overrideSiteId);
  useEffect(() => {
    if (overrideSiteId !== prevOverride.current) {
      prevOverride.current = overrideSiteId;
      setData(null);
      setStreamStatus("connecting");
      setLastUpdated(null);
    }
  }, [overrideSiteId]);

  useEffect(() => {
    let aborted = false;
    let retryDelay = 2000;

    async function connect() {
      try {
        const qs = overrideSiteId ? `?overrideSiteId=${overrideSiteId}` : "";
        const resp = await api.admin.stream(`LiveDashboard/stream${qs}`);

        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }

        const reader = resp.body!.getReader();
        const decoder = new TextDecoder();
        setStreamStatus("live");
        retryDelay = 2000;

        let buffer = "";

        while (!aborted) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const parts = buffer.split("\n\n");
          buffer = parts.pop() ?? "";

          for (const part of parts) {
            for (const line of part.split("\n")) {
              if (line.startsWith("data: ")) {
                try {
                  const raw = JSON.parse(line.slice(6));
                  // Reject frames that are error payloads or missing required shape
                  if (!raw || typeof raw !== "object" || raw.error || !Array.isArray(raw.teams)) continue;
                  const parsed: DashboardData = {
                    ...raw,
                    teams: raw.teams.filter((t: unknown) => t != null && typeof t === "object"),
                    topAgents: Array.isArray(raw.topAgents) ? raw.topAgents.filter((a: unknown) => a != null && typeof a === "object") : [],
                    channelBreakdown: Array.isArray(raw.channelBreakdown) ? raw.channelBreakdown.filter((c: unknown) => c != null && typeof c === "object") : [],
                  };
                  setData(parsed);
                  setLastUpdated(new Date());
                  if (!overrideSiteId) writeCache(parsed);
                } catch {
                  // Ignore malformed SSE frames
                }
              }
            }
          }
        }
      } catch {
        if (!aborted) {
          setStreamStatus("reconnecting");
          await new Promise<void>((r) => setTimeout(r, retryDelay));
          retryDelay = Math.min(retryDelay * 2, 30000); // cap at 30s
          if (!aborted) connect();
        }
      }
    }

    connect();
    return () => {
      aborted = true;
    };
  }, [api, overrideSiteId]);

  return { data, streamStatus, lastUpdated };
}
